import { createContext, ReactNode, useEffect, useState } from "react";
import { PLAYER } from "../types/players";
import { BaseMatchState, GAME_TYPE, OnlineMatchState } from "../types/match";
import { GAME_STATE, OnlineGameState } from "../types/game";
import { useNavigate } from "react-router";
import { ROUTE } from "../types/routes";
import {
  continueGameRequest,
  createPrivateMatch,
  cricketHit,
  denyUndo,
  endLegRequest,
  getGameState,
  getMatchState,
  join301Match,
  join501Match,
  joinCricketMatch,
  joinPrivateMatch,
  restartGameRequest,
  undoRequest,
  x01Hit,
  onPlayerLeave,
} from "../util/onlineControllers";
import { BaseRequest, HitRequest, PrivateMatchRequest } from "../types/online";
import { CRICKET_NUMBERS, Dart, DART_HITS } from "../types/dart";
import { X01GameState } from "../types/x01";
import { CricketGameState } from "../types/cricket";
import OnlineWaitingModal from "../components/onlineWaiting";
import { Alert, Snackbar } from "@mui/material";

export interface OnlineMatch {
  online: boolean;
  onlinePlayer: PLAYER | null;
  id: string | null;
  roomCode: string | null;
  onlineMatchState: BaseMatchState | null;
  onlineGameState: OnlineGameState | null;
  matchPlayers: PLAYER[] | null;
  clearOnlineStates: Function;
  handleSetOnline: Function;
  handleFindCricket: Function;
  handleFind301: Function;
  handleFind501: Function;
  handleJoinPrivate: Function;
  handleCreatePrivate: Function;
  onlineError: string | null;
  handleCricketHit: Function;
  handleX01Hit: Function;
  handleEndLeg: Function;
  handleContinueGame: Function;
  handleRestartGame: Function;
  handleUndoRequest: Function;
  handleDenyUndo: Function;
}

export const OnlineMatchContext = createContext<OnlineMatch>({
  online: false,
  onlinePlayer: null,
  id: null,
  roomCode: null,
  onlineMatchState: null,
  onlineGameState: null,
  onlineError: null,
  matchPlayers: null,
  clearOnlineStates: () => {},
  handleSetOnline: () => {},
  handleFindCricket: async () => {},
  handleFind301: async () => {},
  handleFind501: async () => {},
  handleJoinPrivate: async () => {},
  handleCreatePrivate: async () => {},
  handleCricketHit: async () => {},
  handleX01Hit: async () => {},
  handleEndLeg: async () => {},
  handleContinueGame: async () => {},
  handleRestartGame: async () => {},
  handleUndoRequest: async () => {},
  handleDenyUndo: async () => {},
});

interface Props {
  children?: ReactNode;
}

enum ERRORS {
  FAILED_TO_FETCH = "Failed to fetch",
  COULDNT_CONNECT = "Could not connect to server",
}

export default function OnlineMatchProvider({ children }: Props) {
  const [online, setOnline] = useState(false);
  const [onlinePlayer, setPlayer] = useState<PLAYER | null>(null);
  const [id, setId] = useState<string | null>(null);
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [onlineMatchState, setMatchState] =
    useState<BaseMatchState | null>(null);
  const [onlineGameState, setGameState] = useState<OnlineGameState | null>(null);
  const [onlineError, setOnlineError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [matchPlayers, setMatchPlayers] = useState<PLAYER[] | null>(null);
  const [playerLeft, setPlayerLeft] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.addEventListener("beforeunload", async (e) => {
      if (!online || !onlineGameState || !id || !onlinePlayer || playerLeft)
        return;
      setPlayerLeft(true);
      await onPlayerLeave({ id, player: onlinePlayer });
    });
  });

  useEffect(() => {
    if (!id || !online || !roomCode) return;
    const interval = setInterval(async () => {
      try {
        const { gameState, players } = await getGameState(id);
        if (playerLeft) {
          setPlayerLeft(false);
        }
        if (players !== matchPlayers) {
          setMatchPlayers(players);
        }
        if (!gameState || gameState === onlineGameState) return;
        if (gameState.gameState === GAME_STATE.FINISHED) {
          const matchState = await getMatchState(id);
          setMatchState(matchState.matchState);
          setGameState(matchState.gameState);
          return;
        }
        setGameState(gameState);
      } catch (error: any) {
        let message =
          typeof error?.message === "string"
            ? error.message
            : "Error getting online game state";
        if (error?.message === ERRORS.FAILED_TO_FETCH) {
          message = ERRORS.COULDNT_CONNECT;
        }
        setOnlineError(message);
      }
    }, 2500);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [id, roomCode, online]);
  // Empty array ensures that effect is only run on mount

  const handleSetOnline = (goOnline: boolean) => {
    if (online === goOnline) return;
    setOnline(goOnline);
    if (!goOnline) {
      clearOnlineStates();
    }
  };

  const clearOnlineStates = async () => {
    if (id && onlinePlayer && !playerLeft) {
      onPlayerLeave({ id, player: onlinePlayer });
      setPlayerLeft(true);
    }
    setPlayer(null);
    setId(null);
    setRoomCode(null);
    setMatchState(null);
    setGameState(null);
    setMatchPlayers(null);
  };

  const setAllStates = (match: OnlineMatchState) => {
    if (match?.roomCode && roomCode !== match.roomCode) {
      setRoomCode(match.roomCode);
    }
    if (match?.matchState && onlineMatchState !== match.matchState) {
      setMatchState(match.matchState);
    }
    if (match?.gameState && onlineGameState !== match.gameState) {
      setGameState(match.gameState);
    }
    if (match?.id && id !== match.id) {
      setId(match?.id);
    }
    if (match?.players && !onlinePlayer) {
      const newPlayer = match.players[match.players.length - 1];
      setPlayer(newPlayer);
      setSuccessMessage(`Joined as ${newPlayer}`);
    }
  };

  const handleFindCricket = async () => {
    try {
      const onlineMatch = await joinCricketMatch();
      setAllStates(onlineMatch);
      navigate(ROUTE.CRICKET_PAGE.url);
    } catch (error: any) {
      let message =
        typeof error?.message === "string"
          ? error.message
          : "Error joining cricket match";
      if (error?.message === ERRORS.FAILED_TO_FETCH) {
        message = ERRORS.COULDNT_CONNECT;
      }
      setOnlineError(message);
    }
  };

  const handleFind301 = async () => {
    try {
      const onlineMatch = await join301Match();
      setAllStates(onlineMatch);
      navigate(ROUTE.X01_PAGE.url);
    } catch (error: any) {
      let message =
        typeof error?.message === "string"
          ? error.message
          : "Error joining 301 match";
      if (error?.message === ERRORS.FAILED_TO_FETCH) {
        message = ERRORS.COULDNT_CONNECT;
      }
      setOnlineError(message);
    }
  };

  const handleFind501 = async () => {
    try {
      const onlineMatch = await join501Match();
      setAllStates(onlineMatch);
      navigate(ROUTE.X01_PAGE.url);
    } catch (error: any) {
      let message =
        typeof error?.message === "string"
          ? error.message
          : "Error joining 501 match";
      if (error?.message === ERRORS.FAILED_TO_FETCH) {
        message = ERRORS.COULDNT_CONNECT;
      }
      setOnlineError(message);
    }
  };

  const handleCreatePrivate = async (options: PrivateMatchRequest) => {
    try {
      const onlineMatch = await createPrivateMatch(options);
      setAllStates(onlineMatch);
      if (onlineMatch.gameType === GAME_TYPE.CRICKET) {
        navigate(ROUTE.CRICKET_PAGE.url);
      } else {
        navigate(ROUTE.X01_PAGE.url);
      }
    } catch (error: any) {
      let message =
        typeof error?.message === "string"
          ? error.message
          : "Error creating private match";
      if (error?.message === ERRORS.FAILED_TO_FETCH) {
        message = ERRORS.COULDNT_CONNECT;
      }
      setOnlineError(message);
    }
  };

  const handleJoinPrivate = async (privateRoomCode: string) => {
    try {
      const onlineMatch = await joinPrivateMatch(privateRoomCode);
      setAllStates(onlineMatch);
      if (onlineMatch.gameType === GAME_TYPE.CRICKET) {
        navigate(ROUTE.CRICKET_PAGE.url);
      } else {
        navigate(ROUTE.X01_PAGE.url);
      }
    } catch (error: any) {
      let message =
        typeof error?.message === "string"
          ? error.message
          : "Error joining private match";
      if (error?.message === ERRORS.FAILED_TO_FETCH) {
        message = ERRORS.COULDNT_CONNECT;
      }
      setOnlineError(message);
    }
  };

  const handleCricketHit = async (
    number: CRICKET_NUMBERS,
    multiplier: DART_HITS
  ): Promise<CricketGameState | null> => {
    if (!online || !onlinePlayer || !number || !id || !multiplier || !roomCode)
      return null;
    try {
      const hitRequest: HitRequest = {
        id,
        roomCode,
        player: onlinePlayer,
        number,
        multiplier,
      };
      const onlineMatch = await cricketHit(hitRequest);
      setAllStates(onlineMatch);
      return (onlineMatch.gameState as CricketGameState) ?? null;
    } catch (error: any) {
      let message =
        typeof error?.message === "string"
          ? error.message
          : "Error handling dart";
      if (error?.message === ERRORS.FAILED_TO_FETCH) {
        message = ERRORS.COULDNT_CONNECT;
      }
      setOnlineError(message);
      return null;
    }
  };

  const handleX01Hit = async ({
    number,
    multiplier,
  }: Dart): Promise<X01GameState | null> => {
    if (!online || !onlinePlayer || !number || !id || !multiplier || !roomCode)
      return null;
    try {
      const hitRequest: HitRequest = {
        id,
        roomCode,
        player: onlinePlayer,
        number,
        multiplier,
      };
      const onlineMatch = await x01Hit(hitRequest);
      setAllStates(onlineMatch);
      return (onlineMatch?.gameState as X01GameState) ?? null;
    } catch (error: any) {
      let message =
        typeof error?.message === "string"
          ? error.message
          : "Error handling dart";
      if (error?.message === ERRORS.FAILED_TO_FETCH) {
        message = ERRORS.COULDNT_CONNECT;
      }
      setOnlineError(message);
      return null;
    }
  };

  const handleEndLeg = async () => {
    if (!id || !onlinePlayer) return;
    const request: BaseRequest = {
      id,
      player: onlinePlayer,
    };
    try {
      const onlineMatch = await endLegRequest(request);
      setMatchState(onlineMatch.matchState);
      setGameState(onlineMatch.gameState);
      return onlineMatch.gameState;
    } catch (error: any) {
      let message =
        typeof error?.message === "string" ? error.message : "Error ending leg";
      if (error?.message === ERRORS.FAILED_TO_FETCH) {
        message = ERRORS.COULDNT_CONNECT;
      }
      setOnlineError(message);
      return null;
    }
  };

  const handleContinueGame = async () => {
    if (!id || !onlinePlayer) return;

    try {
      const onlineMatch = await continueGameRequest({
        id,
        player: onlinePlayer,
      });
      setMatchState(onlineMatch.matchState);
      setGameState(onlineMatch.gameState);
      return onlineMatch.gameState;
    } catch (error: any) {
      let message =
        typeof error?.message === "string"
          ? error.message
          : "Error continuing match";
      if (error?.message === ERRORS.FAILED_TO_FETCH) {
        message = ERRORS.COULDNT_CONNECT;
      }
      setOnlineError(message);
      return null;
    }
  };

  const handleRestartGame = async () => {
    if (!id || !onlinePlayer) return;

    try {
      const onlineMatch = await restartGameRequest({
        id,
        player: onlinePlayer,
      });
      setMatchState(onlineMatch.matchState);
      setGameState(onlineMatch.gameState);
      return onlineMatch.gameState;
    } catch (error: any) {
      let message =
        typeof error?.message === "string"
          ? error.message
          : "Error restarting match";
      if (error?.message === ERRORS.FAILED_TO_FETCH) {
        message = ERRORS.COULDNT_CONNECT;
      }
      setOnlineError(message);
      return null;
    }
  };

  const handleUndoRequest = async () => {
    if (!id || !onlinePlayer) return;

    try {
      const onlineGameState = await undoRequest({ id, player: onlinePlayer });
      if (!onlineGameState) return;
      setGameState(onlineGameState);
      return onlineGameState;
    } catch (error: any) {
      let message =
        typeof error?.message === "string"
          ? error.message
          : "Error undo request";
      if (error?.message === ERRORS.FAILED_TO_FETCH) {
        message = ERRORS.COULDNT_CONNECT;
      }
      setOnlineError(message);
      return null;
    }
  };

  const handleDenyUndo = async () => {
    if (!id || !onlinePlayer) return;

    try {
      const onlineGameState = await denyUndo({ id, player: onlinePlayer });
      if (!onlineGameState) return;
      setGameState(onlineGameState);
      return onlineGameState;
    } catch (error: any) {
      let message =
        typeof error?.message === "string"
          ? error.message
          : "Error denying undo";
      if (error?.message === ERRORS.FAILED_TO_FETCH) {
        message = ERRORS.COULDNT_CONNECT;
      }
      setOnlineError(message);
      return null;
    }
  };

  return (
    <OnlineMatchContext.Provider
      value={{
        online,
        onlinePlayer,
        id,
        roomCode,
        onlineMatchState,
        onlineGameState,
        onlineError,
        matchPlayers,
        handleSetOnline,
        handleFindCricket,
        handleFind301,
        handleFind501,
        handleJoinPrivate,
        handleCreatePrivate,
        handleCricketHit,
        handleX01Hit,
        clearOnlineStates,
        handleEndLeg,
        handleContinueGame,
        handleRestartGame,
        handleUndoRequest,
        handleDenyUndo,
      }}
    >
      {onlineError && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={Boolean(onlineError)}
          autoHideDuration={6000}
          onClose={() => setOnlineError(null)}
        >
          <Alert
            variant="standard"
            elevation={6}
            severity="error"
            sx={{ width: "100%" }}
          >
            {onlineError}
          </Alert>
        </Snackbar>
      )}
      {successMessage && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={Boolean(successMessage)}
          autoHideDuration={3000}
          onClose={() => setSuccessMessage(null)}
        >
          <Alert
            variant="standard"
            elevation={6}
            severity="success"
            sx={{ width: "100%" }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
      )}
      {children}
      {(!matchPlayers || matchPlayers.length < 2) && onlineGameState ? (
        <OnlineWaitingModal />
      ) : null}
    </OnlineMatchContext.Provider>
  );
}
